import { useEffect, useMemo, useState, type FormEvent } from "react";
import type { Session } from "@supabase/supabase-js";
import {
  ApiError,
  api,
  setSession,
  setSelectedBusinessId,
  getSelectedBusinessId,
  type EvidenceAsset,
  type Expense,
  type ReportReadiness,
  type ReportVersion,
} from "./api.js";
import { SignIn } from "./SignIn.js";
import { supabase } from "./supabase.js";
import { EvidenceThumbnail } from "./EvidenceThumbnail.js";
import { PrivacyPolicy } from "./PrivacyPolicy.js";
import "./styles.css";

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});
const stateLabel: Record<string, string> = {
  draft: "Saved",
  submitted: "Needs review",
  clarification: "Needs clarification",
  confirmed: "Managed",
  archived: "Archived",
};
// Mirrors the <select name="category"> options in the add/edit form below -
// the ledger's Category column should read the same way the field that set
// it reads, not the raw wire code.
const categoryLabel: Record<string, string> = {
  "schedule-c-meals": "Meals",
  "schedule-c-office": "Office expense",
  "schedule-c-travel": "Travel",
  "schedule-c-other": "Other business expense",
};
function categoryText(expense: Expense): string {
  if (expense.allocations.length > 1) return "Split";
  const code = expense.allocations[0]?.categoryCode;
  return code ? (categoryLabel[code] ?? code) : "—";
}
// "Saved" (not "Verified") matches the iOS app's vocabulary for the same
// server state - see docs/designs/ios-design-completeness-closeout.md's
// Status Vocabulary Mapping. One product, one word per state.
const evidenceStateLabel: Record<string, string> = {
  quarantined: "Still checking",
  scanning: "Still checking",
  available: "Saved",
  rejected: "Rejected",
  failed: "Failed",
};
// Server reason codes (packages/contracts's reportBlockerIds) are internal
// identifiers, not user-facing copy - never render them raw.
const reportBlockerLabel: Record<string, string> = {
  expense_not_confirmed: "not yet confirmed",
  expense_invalid: "missing required information",
};
// "Report outdated" is docs/DESIGN.md's own named status term.
const reportFreshnessLabel: Record<string, string> = {
  current: "Current",
  outdated: "Report outdated",
};

type View = "expenses" | "attention" | "reports" | "settings" | "privacy";

const currentTaxYear = new Date().getFullYear();
function parseTaxYear(): number {
  const fromUrl = Number(
    new URLSearchParams(window.location.search).get("year"),
  );
  return Number.isInteger(fromUrl) && fromUrl >= 2000 && fromUrl <= 2100
    ? fromUrl
    : currentTaxYear;
}

/** URL shape: / or /expense/:id, /attention or /attention/:id, /report,
 * /settings, each with a ?year= query param. Deliberately singular for
 * "expense"/"report" (not "expenses"/"reports") - those plural paths are
 * real API resource prefixes (apps/api/src/worker.ts's API_PATH_PREFIXES),
 * and the Worker routes any request under them to the API, not this SPA. A
 * colliding client route would 401 on refresh/deep-link instead of ever
 * reaching React Router-style client code. "attention" and "settings" don't
 * collide with any API prefix, so they're unchanged.
 * A view keeps its own selection and tax year so browser back/forward and
 * page refresh restore all of it - docs/DESIGN.md: "Browser Back... restore[s]...
 * the business, tax year, query, filters, sort, selection." */
function parseLocation(): {
  view: View;
  selectedId?: string | undefined;
  taxYear: number;
} {
  const [first, second] = window.location.pathname.split("/").filter(Boolean);
  const taxYear = parseTaxYear();
  if (first === "privacy") return { view: "privacy", taxYear };
  if (first === "attention")
    return { view: "attention", selectedId: second, taxYear };
  if (first === "report") return { view: "reports", taxYear };
  if (first === "settings") return { view: "settings", taxYear };
  if (first === "expense")
    return { view: "expenses", selectedId: second, taxYear };
  return { view: "expenses", taxYear };
}
function pathFor(
  view: View,
  selectedId: string | undefined,
  taxYear: number,
): string {
  const base = (() => {
    if (view === "privacy") return "/privacy";
    if (view === "reports") return "/report";
    if (view === "settings") return "/settings";
    const segment = view === "expenses" ? "expense" : view;
    if (!selectedId) return view === "expenses" ? "/" : `/${segment}`;
    return `/${segment}/${selectedId}`;
  })();
  return `${base}${view === "privacy" ? "" : `?year=${taxYear}`}`;
}

export function App() {
  // undefined = still checking for an existing session; null = signed out.
  const [session, setAuthSession] = useState<Session | null | undefined>(
    undefined,
  );
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [location, setLocation] = useState(parseLocation);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [clarifying, setClarifying] = useState(false);
  const [pending, setPending] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [error, setError] = useState("");
  const [evidence, setEvidence] = useState<EvidenceAsset[]>([]);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [readiness, setReadiness] = useState<ReportReadiness>();
  const [reports, setReports] = useState<ReportVersion[]>([]);
  const [settings, setSettings] = useState<{ autoConfirmEnabled: boolean }>();
  const [settingsError, setSettingsError] = useState("");
  const [businesses, setBusinesses] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [selectedBusinessId, setSelectedBusinessIdState] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("selectedBusinessId");
    }
    return null;
  });
  // docs/DESIGN.md: "<768px: never a squeezed desktop table." Reuses the
  // 1000px breakpoint the nav rail already collapses at, so mobile starts
  // exactly where the sidebar disappears rather than a second boundary.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1000,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchPending, setBatchPending] = useState(false);

  const view = location.view;
  const taxYear = location.taxYear;
  // Scoping expenses/attention to the selected tax year is deliberate: a
  // year is the working context the whole app operates in while managing
  // or closing out a filing period, not just a report-generation detail.
  const yearExpenses = useMemo(
    () =>
      expenses.filter(
        (item) => Number(item.transactionDate.slice(0, 4)) === taxYear,
      ),
    [expenses, taxYear],
  );
  const selected = useMemo(
    () => expenses.find((item) => item.id === location.selectedId),
    [expenses, location.selectedId],
  );

  function navigate(
    nextView: View,
    selectedId?: string,
    replace = false,
    taxYear = location.taxYear,
  ) {
    const path = pathFor(nextView, selectedId, taxYear);
    if (replace) window.history.replaceState({}, "", path);
    else if (`${window.location.pathname}${window.location.search}` !== path)
      window.history.pushState({}, "", path);
    setLocation({ view: nextView, selectedId, taxYear });
  }
  function changeTaxYear(year: number) {
    // Changing year keeps the current view but drops any selection - a
    // selected expense from the old year wouldn't be in the new year's
    // filtered list anyway.
    navigate(location.view, undefined, false, year);
  }

  function switchBusiness(businessId: string) {
    setSelectedBusinessIdState(businessId);
    localStorage.setItem("selectedBusinessId", businessId);
    setSelectedBusinessId(businessId);
    setExpenses([]);
  }

  const load = async () => {
    try {
      const data = await api.list();
      setExpenses(data);
      return true;
    } catch (cause) {
      setError(
        cause instanceof Error
          ? userFriendlyError(cause)
          : "Could not load expenses",
      );
      return false;
    }
  };
  useEffect(() => {
    function applySession(next: Session | null) {
      setAuthSession(next);
      setSession(
        next
          ? { accessToken: next.access_token, email: next.user.email ?? null }
          : null,
      );
    }
    supabase.auth.getSession().then(({ data }) => applySession(data.session));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => applySession(next));
    return () => subscription.unsubscribe();
  }, []);
  useEffect(() => {
    if (session) {
      api
        .listBusinesses()
        .then((biz) => {
          setBusinesses(biz);
          if (!selectedBusinessId && biz.length > 0) {
            switchBusiness(biz[0].id);
          } else if (selectedBusinessId) {
            setSelectedBusinessId(selectedBusinessId);
          }
        })
        .catch((err) => {
          console.error("Failed to load businesses:", err);
          setError("Could not load available businesses");
        });
    }
  }, [session]);
  useEffect(() => {
    if (session && selectedBusinessId) void load();
  }, [session, selectedBusinessId]);
  useEffect(() => {
    const onPopState = () => setLocation(parseLocation());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 1000);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // A stale selection from a different view/year/search would let someone
  // bulk-confirm expenses they can no longer see on screen.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [view, taxYear, searchQuery, statusFilter]);
  // First load with no deep link: default to the newest expense in the
  // current tax year, same as before routing existed - but expressed as a
  // real URL now, replacing the initial "/" entry rather than pushing a new
  // history step.
  useEffect(() => {
    if (view === "expenses" && !location.selectedId && yearExpenses.length > 0)
      navigate("expenses", yearExpenses[0]!.id, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearExpenses]);
  useEffect(() => {
    if (!selected) {
      setEvidence([]);
      return;
    }
    let cancelled = false;
    api
      .listEvidence(selected.id)
      .then((items) => {
        if (!cancelled) setEvidence(items);
      })
      .catch(() => {
        if (!cancelled) setEvidence([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selected?.id]);
  useEffect(() => {
    if (view !== "settings") return;
    setSettingsError("");
    api
      .getSettings()
      .then(setSettings)
      .catch((cause) =>
        setSettingsError(
          cause instanceof Error
            ? userFriendlyError(cause)
            : "Could not load settings",
        ),
      );
  }, [view]);
  // Re-fetches whenever the reports view becomes active OR the tax year
  // changes while already there - a bare click-time fetch would miss the
  // second case entirely.
  useEffect(() => {
    if (view !== "reports") return;
    let cancelled = false;
    Promise.all([api.reportReadiness(taxYear), api.reports(taxYear)])
      .then(([nextReadiness, history]) => {
        if (cancelled) return;
        setReadiness(nextReadiness);
        setReports(history);
      })
      .catch((cause) => {
        if (cancelled) return;
        setError(
          cause instanceof Error ? cause.message : "Could not load reports",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [view, taxYear]);
  async function attachReceipt(expense: Expense, file: File) {
    setUploadingEvidence(true);
    setError("");
    try {
      const asset = await api.attachReceipt(expense, file);
      setEvidence((items) => [...items, asset]);
    } catch (cause) {
      await showMutationError(cause, "Could not attach the receipt");
    } finally {
      setUploadingEvidence(false);
    }
  }
  async function generateReport() {
    setGeneratingReport(true);
    setError("");
    try {
      const report = await api.generateReport(taxYear);
      setReports((items) => [report, ...items]);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not generate report",
      );
    } finally {
      setGeneratingReport(false);
    }
  }
  function downloadCsv(report: ReportVersion) {
    const blob = new Blob([report.csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `receipt-ledger-${report.taxYear}-v${report.version}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }
  async function toggleAutoConfirm(next: boolean) {
    setSettingsError("");
    const previous = settings;
    setSettings({ autoConfirmEnabled: next });
    try {
      setSettings(await api.updateSettings({ autoConfirmEnabled: next }));
    } catch (cause) {
      setSettings(previous);
      setSettingsError(
        cause instanceof Error
          ? userFriendlyError(cause)
          : "Could not update settings",
      );
    }
  }
  function replaceExpense(expense: Expense) {
    setExpenses((items) =>
      items.map((item) => (item.id === expense.id ? expense : item)),
    );
  }
  function userFriendlyError(error: Error | ApiError): string {
    const msg = error.message.toLowerCase();
    if (
      msg.includes("401") ||
      msg.includes("unauthorized") ||
      msg.includes("session expired")
    )
      return "Session expired. Please sign in again.";
    if (msg.includes("413") || msg.includes("payload too large"))
      return "File too large. Evidence must be under 25 MB.";
    if (msg.includes("422") || msg.includes("validation"))
      return "Please check your entry and try again.";
    if (msg.includes("500") || msg.includes("server error"))
      return "Server error. Please try again in a moment.";
    if (msg.includes("network") || msg.includes("failed to fetch"))
      return "Network error. Check your connection and try again.";
    return error.message;
  }
  async function showMutationError(cause: unknown, fallback: string) {
    if (cause instanceof ApiError && cause.id === "revision_conflict") {
      if (await load())
        setError(
          "This expense changed elsewhere. We refreshed the latest version; review it and try again.",
        );
      return;
    }
    const message =
      cause instanceof Error ? userFriendlyError(cause) : fallback;
    setError(message);
  }
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const amountMinor = Math.round(Number(form.get("amount")) * 100);
    try {
      const location = String(form.get("location"));
      const input = {
        merchant: String(form.get("merchant")),
        ...(location ? { location } : {}),
        transactionDate: String(form.get("date")),
        amountMinor,
        currency: "USD",
        businessPurpose: String(form.get("purpose")),
        allocations: [
          { categoryCode: String(form.get("category")), amountMinor },
        ],
      };
      const expense =
        editing && selected
          ? await api.revise(selected, input)
          : await api.create(input);
      if (editing) replaceExpense(expense);
      else {
        setExpenses((items) => [expense, ...items]);
        navigate("expenses", expense.id);
      }
      setAdding(false);
      setEditing(false);
      const receipt = form.get("receipt");
      if (receipt instanceof File && receipt.size > 0)
        await attachReceipt(expense, receipt);
    } catch (cause) {
      await showMutationError(cause, "Could not save expense");
    }
  }
  async function command(action: "submit" | "confirm" | "archive" | "restore") {
    if (!selected) return;
    setPending(true);
    setError("");
    try {
      const updated =
        action === "restore"
          ? await api.restore(selected)
          : await api.transition(selected, action);
      replaceExpense(updated);
    } catch (cause) {
      await showMutationError(cause, "Could not update expense");
    } finally {
      setPending(false);
    }
  }
  async function clarify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const reason = String(new FormData(event.currentTarget).get("reason"));
    setPending(true);
    setError("");
    try {
      replaceExpense(await api.clarify(selected, reason));
      setClarifying(false);
    } catch (cause) {
      await showMutationError(cause, "Could not request clarification");
    } finally {
      setPending(false);
    }
  }
  const managed = yearExpenses.filter(
    (expense) => expense.state === "confirmed",
  ).length;
  const attentionExpenses = useMemo(
    () =>
      yearExpenses
        .filter((expense) =>
          ["submitted", "clarification"].includes(expense.state),
        )
        // Severity: clarification blocks the submitter on a specific
        // question, ahead of submitted (awaiting review). Within a
        // severity tier, oldest transaction first - the client has no
        // created/updated timestamp to sort "age" by more precisely.
        .sort((a, b) => {
          if (a.state !== b.state) return a.state === "clarification" ? -1 : 1;
          return a.transactionDate.localeCompare(b.transactionDate);
        }),
    [yearExpenses],
  );
  const attention = attentionExpenses.length;
  // A handful of recent years plus the next one - a raw number input would
  // allow nonsensical years for a field that only ever needs a short,
  // predictable range.
  const yearOptions = Array.from(
    { length: 7 },
    (_, i) => currentTaxYear + 1 - i,
  );
  if (session === undefined) return null;
  if (session === null) return <SignIn />;

  function navLink(label: string, target: View, badge?: number) {
    return (
      <a
        className={view === target ? "active" : ""}
        onClick={() => navigate(target)}
      >
        {label}
        {badge !== undefined && <b>{badge}</b>}
      </a>
    );
  }

  const workspaceItems =
    view === "attention" ? attentionExpenses : yearExpenses;
  const workspaceTitle = view === "attention" ? "Attention" : "Expenses";
  const workspaceHealth =
    view === "attention"
      ? `${attention} need${attention === 1 ? "s" : ""} attention · ordered by severity, then age`
      : `${attention} need attention · ${managed} managed · ${yearExpenses.length - attention - managed} saved`;
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredItems = workspaceItems.filter((expense) => {
    if (statusFilter !== "all" && expense.state !== statusFilter) return false;
    if (!normalizedQuery) return true;
    return (
      expense.merchant.toLowerCase().includes(normalizedQuery) ||
      categoryText(expense).toLowerCase().includes(normalizedQuery)
    );
  });
  const selectedExpenses = filteredItems.filter((expense) =>
    selectedIds.has(expense.id),
  );
  const eligibleSelected = selectedExpenses.filter((expense) =>
    expense.availableActions.includes("confirm"),
  );
  const ineligibleSelectedCount =
    selectedExpenses.length - eligibleSelected.length;
  const selectedTotalMinor = eligibleSelected.reduce(
    (sum, expense) => sum + expense.amountMinor,
    0,
  );
  function toggleSelected(id: string, checked: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }
  async function confirmSelected() {
    if (eligibleSelected.length === 0) return;
    setBatchPending(true);
    setError("");
    const results = await Promise.allSettled(
      eligibleSelected.map((expense) => api.transition(expense, "confirm")),
    );
    const succeededIds = new Set<string>();
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        replaceExpense(result.value);
        succeededIds.add(eligibleSelected[index]!.id);
      }
    });
    const failedCount = results.length - succeededIds.size;
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const id of succeededIds) next.delete(id);
      return next;
    });
    if (failedCount > 0)
      setError(
        `Confirmed ${succeededIds.size} of ${results.length} expenses. ${failedCount} could not be confirmed - review and try again.`,
      );
    setBatchPending(false);
  }

  const yearSelector = (
    <label className="year-switcher">
      <span>Tax year</span>
      <select
        value={taxYear}
        onChange={(event) => changeTaxYear(Number(event.target.value))}
      >
        {yearOptions.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </label>
  );

  function navLink(label: string, targetView: View, count?: number) {
    const isActive = view === targetView;
    return (
      <a
        key={label}
        className={`nav-item ${isActive ? "active" : ""}`}
        onClick={() => navigate(targetView)}
      >
        <span className="nav-label">{label}</span>
        {count !== undefined && count > 0 && (
          <span className="badge-small">{count}</span>
        )}
      </a>
    );
  }

  return (
    <div className="shell">
      {!isNarrow && (
        <aside>
          <div className="brand">Critical Stack Ledger</div>

          {businesses.length > 1 && (
            <div className="business-switcher">
              <span className="label">Business</span>
              <select
                value={selectedBusinessId ?? ""}
                onChange={(e) => switchBusiness(e.target.value)}
              >
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <nav aria-label="Primary" className="nav-items">
            {navLink("Expenses", "expenses")}
            {navLink("Attention", "attention", attention)}
            {navLink("Reports", "reports")}
          </nav>

          <div className="account-section">
            <div className="account-info">
              <small>{session.user.email}</small>
            </div>
            <a
              className={`nav-item account-link ${view === "settings" ? "active" : ""}`}
              onClick={() => navigate("settings")}
            >
              Business settings
            </a>
            <a className="nav-item account-link" onClick={() => void supabase.auth.signOut()}>
              Sign out
            </a>
          </div>
        </aside>
      )}
      {isNarrow && (
        <header className="mobile-topbar">
          <span className="brand">Critical Stack Ledger</span>
          <nav aria-label="Primary" className="nav-items-mobile">
            {navLink("Expenses", "expenses")}
            {navLink("Attention", "attention", attention)}
            {navLink("Reports", "reports")}
          </nav>
        </header>
      )}
      <main>
        {error && (
          <div role="alert" className="alert-error">
            {error}
            <button onClick={() => setError("")}>Dismiss</button>
          </div>
        )}
        {view === "privacy" ? (
          <PrivacyPolicy />
        ) : view === "settings" ? (
          <section className="settings-panel">
            <header className="page-header">
              <div>
                <p className="eyebrow">Account</p>
                <h1>Settings</h1>
              </div>
            </header>
            <div className="settings-content">
              <div className="settings-section">
                <h2>Business</h2>
                {businesses.length > 1 && (
                  <div className="settings-item">
                    <label>
                      <span className="label-text">Switch business:</span>
                      <select
                        value={selectedBusinessId ?? ""}
                        onChange={(e) => switchBusiness(e.target.value)}
                      >
                        {businesses.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
              </div>
              <div className="settings-section">
                <h2>Confirmation</h2>
                {settingsError && (
                  <div role="alert" className="alert-error">
                    {settingsError}
                  </div>
                )}
                {!settings ? (
                  <p>Loading…</p>
                ) : (
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={settings.autoConfirmEnabled}
                      onChange={(event) =>
                        void toggleAutoConfirm(event.target.checked)
                      }
                    />
                    <span>
                      <strong>Auto-confirm submitted expenses</strong>
                      <small>
                        When on, expenses with no exceptions move straight to
                        Managed instead of waiting for manual confirmation.
                      </small>
                    </span>
                  </label>
                )}
              </div>
              {isNarrow && (
                <button className="secondary" onClick={() => navigate("expenses")}>
                  ← Back
                </button>
              )}
            </div>
          </section>
        ) : view === "reports" ? (
          <section className="reports-panel">
            <header className="page-header">
              <div>
                <p className="eyebrow">{taxYear} reporting</p>
                <h1>Report readiness</h1>
              </div>
            </header>
            <div className="reports-content">
              <p>
                {readiness?.status === "ready"
                  ? "Ready to generate from confirmed expenses."
                  : "Resolve the items below before generating."}
              </p>
              {readiness?.blockers.length ? (
                <div className="blockers-list">
                  {readiness.blockers.map((blocker) => {
                    const blockedExpense = expenses.find(
                      (item) => item.id === blocker.expenseId,
                    );
                    const reasonText = blocker.reasons
                      .map((reason) => reportBlockerLabel[reason] ?? reason)
                      .join(", ");
                    return (
                      <button
                        key={blocker.expenseId}
                        className="alert-attention"
                        onClick={() => navigate("expenses", blocker.expenseId)}
                      >
                        <strong>{blockedExpense?.merchant ?? "An expense"}</strong>
                        <span>{reasonText}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <h2>Report history</h2>
              {reports.length === 0 ? (
                <p>No report versions yet.</p>
              ) : (
                reports.map((report) => (
                  <article className="report-card" key={report.id}>
                    <div className="report-card-header">
                      <strong>Version {report.version}</strong>
                      <span
                        className={`status-badge ${report.freshness === "outdated" ? "attention" : "confirmed"}`}
                      >
                        {reportFreshnessLabel[report.freshness] ?? report.freshness}
                      </span>
                    </div>
                    <small>
                      {report.expenseIds.length} expenses ·{" "}
                      {report.checksum.slice(0, 12)}…
                    </small>
                    <div className="report-card-actions">
                      <button
                        className="secondary"
                        onClick={() => downloadCsv(report)}
                      >
                        Download CSV
                      </button>
                    </div>
                    <details>
                      <summary>View CSV</summary>
                      <pre>{report.csv}</pre>
                    </details>
                  </article>
                ))
              )}
              <button
                className="primary"
                onClick={() => void generateReport()}
                disabled={readiness?.status !== "ready" || generatingReport}
              >
                {generatingReport ? "Generating…" : "Generate report"}
              </button>
              {isNarrow && (
                <button className="secondary" onClick={() => navigate("expenses")}>
                  ← Back
                </button>
              )}
            </div>
          </section>
        ) : (
          <section className="workspace">
            {(!isNarrow || !selected) && (
              <div className="ledger">
                <header className="page-header">
                  <div>
                    <p className="eyebrow">{taxYear} expense ledger</p>
                    <h1>{workspaceTitle}</h1>
                    <p className="subtitle">{workspaceHealth}</p>
                  </div>
                  {view === "expenses" && (
                    <button className="primary" onClick={() => setAdding(true)}>
                      Add expense
                    </button>
                  )}
                </header>

                <div className="summary-cards">
                  <div className="summary-card">
                    <div className="card-label">Total amount</div>
                    <div className="card-value">
                      {money.format(
                        yearExpenses.reduce((sum, e) => sum + e.amountMinor, 0) / 100
                      )}
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="card-label">Confirmed expenses</div>
                    <div className="card-value">
                      {yearExpenses.filter((e) => e.state === "confirmed").length}
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="card-label">Need attention</div>
                    <div className="card-value attention-count">
                      {attention}
                    </div>
                  </div>
                </div>

                <div className="toolbar">
                  <input
                    type="search"
                    aria-label="Search expenses"
                    placeholder="Search by merchant or category"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="search-input"
                  />
                  <select
                    aria-label="Filter by status"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className="status-filter"
                  >
                    <option value="all">All statuses</option>
                    <option value="draft">Saved</option>
                    <option value="submitted">Needs review</option>
                    <option value="clarification">Needs clarification</option>
                    <option value="confirmed">Managed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="table-container">
                  <div className="table-head">
                    <span className="table-checkbox-column" aria-hidden="true" />
                    <div className="table-columns">
                      <span>Vendor</span>
                      <span>Category</span>
                      <span>Date</span>
                      <span>Amount</span>
                      <span></span>
                    </div>
                  </div>
                  {filteredItems.length === 0 ? (
                    <div className="empty-state">
                      {workspaceItems.length > 0 ? (
                        <>
                          <h2>No matching expenses</h2>
                          <p>Try a different search term or status filter.</p>
                        </>
                      ) : view === "attention" ? (
                        <>
                          <h2>Nothing needs attention</h2>
                          <p>New receipts will appear here if they need you.</p>
                        </>
                      ) : (
                        <>
                          <h2>No expenses yet</h2>
                          <p>
                            Add the first business expense. Its history starts the
                            moment you save it.
                          </p>
                          <button className="primary" onClick={() => setAdding(true)}>
                            Add expense
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="table-body">
                      {filteredItems.map((expense) => (
                        <div
                          className={`table-row ${selected?.id === expense.id ? "selected" : ""}`}
                          key={expense.id}
                        >
                          <input
                            type="checkbox"
                            className="row-checkbox"
                            aria-label={`Select ${expense.merchant}`}
                            checked={selectedIds.has(expense.id)}
                            onChange={(event) =>
                              toggleSelected(expense.id, event.target.checked)
                            }
                          />
                          <button
                            className="row-content"
                            onClick={() => navigate(view, expense.id)}
                          >
                            <strong className="merchant">{expense.merchant}</strong>
                            <span className="category">{categoryText(expense)}</span>
                            <span className="date">{expense.transactionDate}</span>
                            <span className="amount">
                              {money.format(expense.amountMinor / 100)}
                            </span>
                            <span className={`status-indicator ${expense.state}`} title={stateLabel[expense.state]}>
                              {stateLabel[expense.state]}
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedExpenses.length > 0 && (
                  <div className="batch-actions">
                    <div className="batch-info">
                      <strong>
                        {eligibleSelected.length} expense
                        {eligibleSelected.length === 1 ? "" : "s"} selected ·{" "}
                        {money.format(selectedTotalMinor / 100)}
                      </strong>
                      {ineligibleSelectedCount > 0 && (
                        <p className="batch-warning">
                          {ineligibleSelectedCount} ineligible record
                          {ineligibleSelectedCount === 1
                            ? " was"
                            : "s were"}{" "}
                          left out.
                        </p>
                      )}
                    </div>
                    <button
                      className="primary"
                      disabled={eligibleSelected.length === 0 || batchPending}
                      onClick={() => void confirmSelected()}
                    >
                      {batchPending
                        ? "Confirming…"
                        : `Review and confirm ${eligibleSelected.length}`}
                    </button>
                  </div>
                )}
              </div>
            )}
            {(!isNarrow || selected) && (
              <aside className="detail-panel">
                {selected ? (
                  <>
                    {isNarrow && (
                      <button
                        className="secondary back-button"
                        onClick={() => navigate(view)}
                      >
                        ← Back
                      </button>
                    )}
                    <div className="detail-header">
                      <p className="eyebrow">Expense #{selected.revision}</p>
                      <h2>{selected.merchant}</h2>
                      <span className={`status-badge large ${selected.state}`}>
                        {stateLabel[selected.state]}
                      </span>
                    </div>
                    {selected.state === "clarification" &&
                      selected.clarificationReason && (
                        <div className="alert-clarification">
                          <strong>Needs clarification:</strong>
                          <p>{selected.clarificationReason}</p>
                        </div>
                      )}
                    <div className="evidence-section">
                      {evidence.length === 0 ? (
                        <div className="evidence-placeholder">
                          <span>No receipt attached</span>
                          <small>Manual entry</small>
                        </div>
                      ) : (
                        <div className="evidence-list">
                          {evidence.map((asset) => (
                            <div key={asset.id} className="evidence-item">
                              {asset.state === "available" && (
                                <EvidenceThumbnail assetId={asset.id} />
                              )}
                              <span className={`status-badge ${asset.state}`}>
                                {evidenceStateLabel[asset.state]}
                              </span>
                              {asset.failure && (
                                <small className="failure-message">
                                  {asset.failure.replaceAll("_", " ")}
                                </small>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <label className="file-input-label">
                        {uploadingEvidence ? "Uploading…" : "Attach a receipt"}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,application/pdf"
                          disabled={uploadingEvidence}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (file && selected)
                              void attachReceipt(selected, file);
                          }}
                        />
                      </label>
                    </div>

                    <div className="detail-info">
                      <div className="info-group">
                        <label>Transaction date</label>
                        <span className="info-value">{selected.transactionDate}</span>
                      </div>
                      <div className="info-group">
                        <label>Business purpose</label>
                        <span className="info-value">{selected.businessPurpose}</span>
                      </div>
                      <div className="info-group">
                        <label>Category</label>
                        <span className="info-value">{selected.allocations[0]?.categoryCode}</span>
                      </div>
                      <div className="info-group amount-group">
                        <label>Amount</label>
                        <span className="amount-value">
                          {money.format(selected.amountMinor / 100)}
                        </span>
                      </div>
                    </div>
                    <div className="action-buttons">
                      {selected.availableActions.includes("submit") && (
                        <button
                          className="primary full-width"
                          disabled={pending}
                          onClick={() => void command("submit")}
                        >
                          Submit expense
                        </button>
                      )}
                      {selected.availableActions.includes("confirm") && (
                        <button
                          className="primary full-width"
                          disabled={pending}
                          onClick={() => void command("confirm")}
                        >
                          Confirm as managed
                        </button>
                      )}
                      {selected.availableActions.includes("clarify") && (
                        <button
                          className="secondary full-width"
                          disabled={pending}
                          onClick={() => setClarifying(true)}
                        >
                          Request clarification
                        </button>
                      )}
                      {selected.availableActions.includes("revise") && (
                        <button
                          className="secondary full-width"
                          disabled={pending}
                          onClick={() => setEditing(true)}
                        >
                          Revise expense
                        </button>
                      )}
                      {selected.availableActions.includes("archive") && (
                        <button
                          className="danger full-width"
                          disabled={pending}
                          onClick={() => {
                            if (
                              window.confirm(
                                "Archive this expense? It will leave active queues but remain in audit history.",
                              )
                            )
                              void command("archive");
                          }}
                        >
                          Archive expense
                        </button>
                      )}
                      {selected.availableActions.includes("restore") && (
                        <button
                          className="primary full-width"
                          disabled={pending}
                          onClick={() => void command("restore")}
                        >
                          Restore expense
                        </button>
                      )}
                    </div>
                    {selected.state === "confirmed" && (
                      <div className="success-note">
                        ✓ Expense managed · revision {selected.revision}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state">
                    <p>Select an expense to view its details.</p>
                  </div>
                )}
              </aside>
            )}
          </section>
        )}
      </main>
      {clarifying && selected && (
        <div className="modal-overlay">
          <form className="modal-dialog" onSubmit={(event) => void clarify(event)}>
            <header className="modal-header">
              <div>
                <p className="eyebrow">Needs clarification</p>
                <h2>Ask a specific question</h2>
              </div>
              <button
                type="button"
                className="close-button"
                onClick={() => setClarifying(false)}
              >
                ✕
              </button>
            </header>
            <div className="modal-body">
              <label className="form-label">
                <span>Question for the submitter</span>
                <textarea name="reason" required autoFocus />
              </label>
            </div>
            <div className="modal-footer">
              <button className="primary full-width" type="submit" disabled={pending}>
                Send for clarification
              </button>
            </div>
          </form>
        </div>
      )}
      {(adding || (editing && selected)) && (
        <div className="modal-overlay">
          <form className="modal-dialog" onSubmit={(event) => void save(event)}>
            <header className="modal-header">
              <div>
                <p className="eyebrow">Manual entry</p>
                <h2>{editing ? "Revise expense" : "Add expense"}</h2>
              </div>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setAdding(false);
                  setEditing(false);
                }}
              >
                ✕
              </button>
            </header>
            <div className="modal-body">
              <label className="form-label">
                <span>Merchant</span>
                <input
                  name="merchant"
                  required
                  autoFocus
                  defaultValue={editing ? selected?.merchant : ""}
                />
              </label>
              <label className="form-label">
                <span>Location</span>
                <input
                  name="location"
                  defaultValue={editing ? selected?.location : ""}
                />
              </label>
              <div className="form-row">
                <label className="form-label">
                  <span>Date</span>
                  <input
                    name="date"
                    type="date"
                    required
                    defaultValue={editing ? selected?.transactionDate : ""}
                  />
                </label>
                <label className="form-label">
                  <span>Amount</span>
                  <input
                    name="amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    defaultValue={
                      editing && selected ? selected.amountMinor / 100 : undefined
                    }
                  />
                </label>
              </div>
              <label className="form-label">
                <span>Business purpose</span>
                <textarea
                  name="purpose"
                  required
                  defaultValue={editing ? selected?.businessPurpose : ""}
                />
              </label>
              <label className="form-label">
                <span>Schedule C category</span>
                <select
                  name="category"
                  defaultValue={
                    editing
                      ? selected?.allocations[0]?.categoryCode
                      : "schedule-c-other"
                  }
                >
                  <option value="schedule-c-meals">Meals</option>
                  <option value="schedule-c-office">Office expense</option>
                  <option value="schedule-c-travel">Travel</option>
                  <option value="schedule-c-other">Other business expense</option>
                </select>
              </label>
              {!editing && (
                <label className="form-label">
                  <span>Receipt (optional)</span>
                  <input
                    name="receipt"
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                  />
                </label>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="primary full-width"
                type="submit"
                disabled={uploadingEvidence}
              >
                {uploadingEvidence
                  ? "Uploading receipt…"
                  : editing
                    ? "Save revision"
                    : "Save expense"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
